import { connectionAtom, networkAtom } from "@/atoms/settings"
import { nativeTokenBalanceAtom } from "@/atoms/wallet"
import { useRenecConnect } from "@/libs/wallet-provider/renec-provider/useRenecConnect"
import { useSolanaConnect } from "@/libs/wallet-provider/solana-provider/useSolanaConnect"
import { CustomConnection } from "@/utils/on_chain/custom-connection"
import { BlockchainNetwork, ConstantsHelper } from "@renec-foundation/relend-adapter"
import { Wallet } from "@solana/wallet-adapter-react"
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js"
import { useAtomValue, useSetAtom } from "jotai"
import { useCallback, useEffect, useMemo } from "react"

type ButtonState = {
  buttonState: "connecting" | "connected" | "disconnecting" | "renec-has-wallet" | "solana-has-wallet" | "no-wallet"
  onConnect?: () => void
  onDisconnect?: () => void
  onSelectWallet?: () => void
  publicKey?: PublicKey
  walletIcon?: Wallet["adapter"]["icon"]
  walletName?: Wallet["adapter"]["name"]
}

const useWalletConnect = () => {
  const {
    solanaWallet,
    solanaPubkeyBs58,
    solanaConnected,
    solanaConnecting,
    solanaDisconnecting,
    setSolanaVisible,
    connectSolana,
    disconnectWallet: disconnectSolanaWallet,
  } = useSolanaConnect()
  const {
    renecWallet,
    renecPubkeyBs58,
    renecConnected,
    renecConnecting,
    renecDisconnecting,
    setRenecVisible,
    disconnectWallet: disconnectRenecWallet,
  } = useRenecConnect()
  const setNativeTokenBalance = useSetAtom(nativeTokenBalanceAtom)

  const network = useAtomValue(networkAtom)
  const connection = useAtomValue(connectionAtom)

  const isRenecChain = ConstantsHelper.isRenecChain(network)
  const isSolanaChain = ConstantsHelper.isSolanaChain(network)

  let buttonState: ButtonState["buttonState"]
  if ((solanaConnecting && isSolanaChain) || (renecConnecting && isRenecChain)) {
    buttonState = "connecting"
  } else if ((solanaConnected && isSolanaChain) || (renecConnected && isRenecChain)) {
    buttonState = "connected"
  } else if ((solanaDisconnecting && isSolanaChain) || (renecDisconnecting && isRenecChain)) {
    buttonState = "disconnecting"
  } else if (solanaWallet && isSolanaChain) {
    buttonState = "solana-has-wallet"
  } else if (isRenecChain) {
    buttonState = "renec-has-wallet"
  } else {
    buttonState = "no-wallet"
  }

  const connectWallet = useCallback(
    (network: BlockchainNetwork) => {
      if (ConstantsHelper.isSolanaChain(network)) {
        if (solanaWallet) {
          return connectSolana()
        } else {
          return setSolanaVisible(true)
        }
      }

      return setRenecVisible(true)
    },
    [connectSolana, setRenecVisible, setSolanaVisible, solanaWallet],
  )

  const address = useMemo(() => {
    if (ConstantsHelper.isSolanaChain(network)) {
      return solanaPubkeyBs58
    }
    if (ConstantsHelper.isRenecChain(network)) {
      return renecPubkeyBs58
    }
    return undefined
  }, [solanaPubkeyBs58, network, renecPubkeyBs58])

  const wallet = useMemo(() => {
    if (ConstantsHelper.isSolanaChain(network)) {
      return solanaWallet
    }
    if (ConstantsHelper.isRenecChain(network)) {
      return renecWallet
    }
    return null
  }, [network, solanaWallet, renecWallet])

  const connected = useMemo(() => {
    if (ConstantsHelper.isSolanaChain(network)) {
      return solanaConnected
    }
    if (ConstantsHelper.isRenecChain(network)) {
      return renecConnected
    }
    return false
  }, [network, renecConnected, solanaConnected])

  const openChangeWalletModal = useCallback(() => {
    if (isSolanaChain) {
      setSolanaVisible(true)
    } else {
      setRenecVisible(true)
    }
  }, [isSolanaChain, setRenecVisible, setSolanaVisible])

  const disconnectWallet = useCallback(async () => {
    if (isRenecChain) {
      return disconnectRenecWallet()
    } else {
      return disconnectSolanaWallet()
    }
  }, [isRenecChain, disconnectRenecWallet, disconnectSolanaWallet])

  const getBalanceNativeToken = useCallback(
    async (abortController?: AbortController) => {
      try {
        const walletPubkey = isRenecChain ? renecPubkeyBs58 : solanaPubkeyBs58
        if (address && walletPubkey) {
          const customConnection = new CustomConnection(connection)
          return (
            ((await customConnection.getBalanceWithAbortSignal(
              new PublicKey(walletPubkey),
              undefined,
              abortController,
            )) || 0) / LAMPORTS_PER_SOL
          )
        }
        return 0
      } catch (err: any) {
        console.error(err)
        return 0
      }
    },
    [address, connection, isRenecChain, renecPubkeyBs58, solanaPubkeyBs58],
  )

  useEffect(() => {
    const fetchNativeBalance = async () => {
      setNativeTokenBalance(await getBalanceNativeToken())
    }
    fetchNativeBalance()
  }, [getBalanceNativeToken, setNativeTokenBalance])

  return {
    wallet,
    network,
    address,
    connected,
    connectWallet,
    solanaWallet,
    solanaPubkeyBs58,
    renecWallet,
    renecPubkeyBs58,
    getBalanceNativeToken,
    disconnectWallet,
    isRenecChain,
    isSolanaChain,
    buttonState,
    openChangeWalletModal,
  }
}

export default useWalletConnect
